import { Container, Graphics } from 'pixi.js'
import { NeonColorsHex } from '../ui/neon-theme'

interface Particle {
  x: number
  z: number
  vx: number
  vz: number
  life: number
  maxLife: number
  size: number
  color: number
  alpha: number
  graphic: Graphics
}

type EffectType =
  | 'death_explosion'
  | 'death_sparks'
  | 'death_scatter_land'
  | 'spark_collect'
  | 'boost_trail'
  | 'portal_vortex'
  | 'zone_fire'
  | 'zone_damage'
  | 'kill_flash'
  | 'evacuation_warp'
  | 'spawn_materialize'
  | 'spawn_scatter'

interface EffectConfig {
  count: number
  speed: number
  life: number
  size: number
  colors: number[]
  gravity?: number
  fadeOut?: boolean
  shrink?: boolean
}

interface ParticleViewport {
  x: number
  z: number
  width: number
  height: number
}

const EFFECT_CONFIGS: Record<EffectType, EffectConfig> = {
  death_explosion: {
    count: 24,
    speed: 3000,
    life: 1.0,
    size: 40,
    colors: [NeonColorsHex.gold, NeonColorsHex.primary, 0xFFAA44, NeonColorsHex.danger, 0xFFFFFF],
    gravity: 400,
    fadeOut: true,
    shrink: true,
  },
  death_sparks: {
    count: 8,
    speed: 600,
    life: 1.2,
    size: 30,
    colors: [NeonColorsHex.gold, NeonColorsHex.primary, 0xFF8844, 0xFFFF44],
    fadeOut: true,
    shrink: false,
  },
  death_scatter_land: {
    count: 2,
    speed: 700,
    life: 0.45,
    size: 18,
    colors: [NeonColorsHex.gold, NeonColorsHex.primary, 0xFFFFAA, 0xFFFFFF],
    fadeOut: true,
    shrink: true,
  },
  spark_collect: {
    count: 6,
    speed: 800,
    life: 0.4,
    size: 20,
    colors: [NeonColorsHex.gold, NeonColorsHex.primary, 0xFFFF00],
    fadeOut: true,
    shrink: true,
  },
  boost_trail: {
    count: 3,
    speed: 300,
    life: 0.3,
    size: 35,
    colors: [NeonColorsHex.primary, NeonColorsHex.info, 0xFFAA77],
    fadeOut: true,
    shrink: true,
  },
  portal_vortex: {
    count: 8,
    speed: 600,
    life: 1.0,
    size: 25,
    colors: [NeonColorsHex.portal, NeonColorsHex.portalGlow, 0xAA77FF, 0x4400CC],
    fadeOut: true,
  },
  zone_fire: {
    count: 5,
    speed: 400,
    life: 0.6,
    size: 30,
    colors: [0xFF2200, NeonColorsHex.danger, 0xFF6600, 0xFF0000],
    gravity: -300,
    fadeOut: true,
  },
  zone_damage: {
    count: 4,
    speed: 500,
    life: 0.5,
    size: 25,
    colors: [NeonColorsHex.danger, 0xCC0000],
    fadeOut: true,
    shrink: true,
  },
  kill_flash: {
    count: 15,
    speed: 1500,
    life: 0.5,
    size: 35,
    colors: [NeonColorsHex.textBright, NeonColorsHex.gold, NeonColorsHex.primary],
    fadeOut: true,
    shrink: true,
  },
  evacuation_warp: {
    count: 20,
    speed: 1200,
    life: 1.2,
    size: 30,
    colors: [NeonColorsHex.portal, NeonColorsHex.info, NeonColorsHex.textBright],
    fadeOut: true,
    shrink: true,
  },
  spawn_materialize: {
    count: 25,
    speed: 1000,
    life: 0.6,
    size: 30,
    colors: [NeonColorsHex.success, NeonColorsHex.info, NeonColorsHex.textBright],
    fadeOut: true,
    shrink: true,
  },
  spawn_scatter: {
    count: 15,
    speed: 800,
    life: 0.5,
    size: 25,
    colors: [NeonColorsHex.gold, NeonColorsHex.primary, 0xFFFF00],
    fadeOut: true,
    shrink: true,
  },
}

const MAX_PARTICLES = 500
const POOL_INITIAL_SIZE = 100
const PARTICLE_VIEW_MARGIN = 360

export class ParticleManager {
  private container: Container
  private particles: Particle[] = []
  private pool: Graphics[] = []
  private viewport: ParticleViewport | null = null

  constructor(parentContainer: Container) {
    this.container = new Container()
    parentContainer.addChild(this.container)

    // Pre-allocate pool of Graphics objects
    for (let i = 0; i < POOL_INITIAL_SIZE; i++) {
      this.pool.push(new Graphics())
    }
  }

  emit(type: EffectType, x: number, z: number): void {
    const config = EFFECT_CONFIGS[type]

    for (let i = 0; i < config.count; i++) {
      if (this.particles.length >= MAX_PARTICLES) {
        this.recycleOldest()
      }

      const angle = Math.random() * Math.PI * 2
      const speed = config.speed * (0.5 + Math.random() * 0.5)
      const color = config.colors[Math.floor(Math.random() * config.colors.length)]

      // Take from pool or create new if pool is empty
      const graphic = this.pool.length > 0 ? this.pool.pop()! : new Graphics()
      graphic.clear()
      graphic.circle(0, 0, config.size)
      graphic.fill({ color, alpha: 1 })
      graphic.x = x
      graphic.y = z
      graphic.alpha = 1
      graphic.scale.set(1)
      graphic.visible = true
      this.container.addChild(graphic)

      this.particles.push({
        x,
        z,
        vx: Math.cos(angle) * speed,
        vz: Math.sin(angle) * speed,
        life: config.life,
        maxLife: config.life,
        size: config.size,
        color,
        alpha: 1,
        graphic,
      })
    }
  }

  setViewport(viewport: ParticleViewport | null): void {
    this.viewport = viewport
  }

  update(dt: number): void {
    const viewport = this.viewport
    const minX = viewport ? viewport.x - PARTICLE_VIEW_MARGIN : Number.NEGATIVE_INFINITY
    const minZ = viewport ? viewport.z - PARTICLE_VIEW_MARGIN : Number.NEGATIVE_INFINITY
    const maxX = viewport ? viewport.x + viewport.width + PARTICLE_VIEW_MARGIN : Number.POSITIVE_INFINITY
    const maxZ = viewport ? viewport.z + viewport.height + PARTICLE_VIEW_MARGIN : Number.POSITIVE_INFINITY
    let i = 0
    while (i < this.particles.length) {
      const p = this.particles[i]
      p.life -= dt

      if (p.life <= 0) {
        // Return to pool instead of destroying
        this.container.removeChild(p.graphic)
        p.graphic.visible = false
        this.pool.push(p.graphic)
        // Swap with last element for O(1) removal
        this.particles[i] = this.particles[this.particles.length - 1]
        this.particles.pop()
        continue
      }

      // Move
      p.x += p.vx * dt
      p.z += p.vz * dt

      // Apply friction
      p.vx *= 0.98
      p.vz *= 0.98

      const inViewport = p.x >= minX && p.x <= maxX && p.z >= minZ && p.z <= maxZ
      if (!inViewport) {
        p.graphic.visible = false
        i++
        continue
      }

      // Update graphic
      const lifeRatio = p.life / p.maxLife
      p.graphic.visible = true
      p.graphic.x = p.x
      p.graphic.y = p.z
      p.graphic.alpha = lifeRatio
      p.graphic.scale.set(lifeRatio)

      i++
    }
  }

  private recycleOldest(): void {
    if (this.particles.length === 0) return

    // Find particle with lowest life
    let oldestIdx = 0
    let lowestLife = this.particles[0].life
    for (let i = 1; i < this.particles.length; i++) {
      if (this.particles[i].life < lowestLife) {
        lowestLife = this.particles[i].life
        oldestIdx = i
      }
    }

    const p = this.particles[oldestIdx]
    this.container.removeChild(p.graphic)
    p.graphic.visible = false
    this.pool.push(p.graphic)
    // Swap with last for O(1) removal
    this.particles[oldestIdx] = this.particles[this.particles.length - 1]
    this.particles.pop()
  }

  clear(): void {
    for (const p of this.particles) {
      this.container.removeChild(p.graphic)
      p.graphic.visible = false
      this.pool.push(p.graphic)
    }
    this.particles.length = 0
    this.viewport = null
  }

  dispose(): void {
    this.clear()
    // Destroy all pooled graphics
    for (const gfx of this.pool) {
      gfx.destroy()
    }
    this.pool.length = 0
    this.container.parent?.removeChild(this.container)
    this.container.destroy()
  }

  getActiveParticleCount(): number {
    return this.particles.length
  }
}
